import React from 'react';
import { ArrowUpRight, ArrowDownLeft, Plus, Shield, TrendingUp, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Layout/Header';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { SendModal } from '../components/Modals/SendModal';
import { ReceiveModal } from '../components/Modals/ReceiveModal';
import { useWalletStore } from '../store/walletStore';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, balance, assets, transactions, connect } = useWalletStore();
  const [showBalance, setShowBalance] = React.useState(true);
  const [sendModalOpen, setSendModalOpen] = React.useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = React.useState(false);

  // Demo wallet address for receive functionality
  const demoWalletAddress = 'ALGO7X2K4EXAMPLEWALLETADDRESS123456789ABCDEFGHIJKLMNOP';

  const totalValue = assets.reduce((sum, asset) => sum + (asset.balance * asset.value), 0);
  const algoBalance = assets.find(asset => asset.symbol === 'ALGO')?.balance || 0;

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <img src="/wallwet.webp" alt="Algowise" className="w-12 h-12 rounded-full" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Algowise</h1>
            <p className="text-gray-600">Your gateway to the Algorand ecosystem</p>
          </div>
          <Button onClick={connect} fullWidth size="lg">
            Connect Wallet
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />
      
      {/* Balance Card */}
      <div className="p-4">
        <Card className="bg-gradient-to-br from-primary-600 to-primary-700 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-primary-100 text-sm">Total Balance</p>
              <div className="flex items-center space-x-2">
                <h2 className="text-3xl font-bold">
                  {showBalance ? `$${totalValue.toFixed(2)}` : '••••••'}
                </h2>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="p-1 rounded-full hover:bg-primary-500 transition-colors"
                >
                  {showBalance ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center text-green-300">
                <TrendingUp size={16} className="mr-1" />
                <span className="text-sm">+12%</span>
              </div>
              <p className="text-primary-100 text-xs">24h change</p>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex space-x-3">
            <Button 
              variant="ghost" 
              className="flex-1 bg-white/10 text-white hover:bg-white/20"
              onClick={() => setSendModalOpen(true)}
            >
              <ArrowUpRight size={16} className="mr-2" />
              Send
            </Button>
            <Button 
              variant="ghost" 
              className="flex-1 bg-white/10 text-white hover:bg-white/20"
              onClick={() => setReceiveModalOpen(true)}
            >
              <ArrowDownLeft size={16} className="mr-2" />
              Receive
            </Button>
            <Button 
              variant="ghost" 
              className="flex-1 bg-white/10 text-white hover:bg-white/20"
              onClick={() => navigate('/buy')}
            >
              <Plus size={16} className="mr-2" />
              Buy
            </Button>
          </div>
        </Card>
      </div>

      {/* Scam Alert */}
      <div className="px-4 mb-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <div className="flex items-start space-x-3">
            <Shield size={20} className="text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">Security Alert</h3>
              <p className="text-sm text-yellow-700">
                Always verify transaction details before confirming. Algowise will never ask for your seed phrase.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Assets */}
      <div className="px-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Your Assets</h3>
          <Button variant="ghost" size="sm">View All</Button>
        </div>
        
        <div className="space-y-3">
          {assets.map((asset) => (
            <Card key={asset.id} hover>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="font-bold text-primary-600 text-sm">
                      {asset.symbol.slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{asset.name}</p>
                    <p className="text-sm text-gray-500">{asset.balance.toFixed(2)} {asset.symbol}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    ${(asset.balance * asset.value).toFixed(2)}
                  </p>
                  <p className={`text-sm ${asset.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {asset.change24h >= 0 ? '+' : ''}{asset.change24h.toFixed(1)}%
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <Button variant="ghost" size="sm">View All</Button>
        </div>
        
        <div className="space-y-3">
          {transactions.slice(0, 3).map((tx) => (
            <Card key={tx.id} hover>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    tx.type === 'receive' ? 'bg-green-100' : 
                    tx.type === 'send' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    {tx.type === 'receive' ? (
                      <ArrowDownLeft size={16} className="text-green-600" />
                    ) : tx.type === 'send' ? (
                      <ArrowUpRight size={16} className="text-red-600" />
                    ) : (
                      <TrendingUp size={16} className="text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 capitalize">{tx.type}</p>
                    <p className="text-sm text-gray-500">
                      {tx.timestamp.toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    tx.type === 'receive' ? 'text-green-600' : 'text-gray-900'
                  }`}>
                    {tx.type === 'receive' ? '+' : '-'}{tx.amount} {tx.asset}
                  </p>
                  <p className="text-sm text-gray-500 capitalize">{tx.status}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Modals */}
      <SendModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        availableBalance={algoBalance}
      />
      
      <ReceiveModal
        isOpen={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        walletAddress={demoWalletAddress}
      />
    </div>
  );
};